import React, { useState, useEffect, useRef } from "react";

import closeIcon from "../../Images/close-icon.svg";
import { COUNTER_THEMES } from "../../utils/counterTheme";

// -------------------------
// Icons
// -------------------------
function CopyIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M8 8V5.2c0-1.1.9-2 2-2h8.8c1.1 0 2 .9 2 2v8.8c0 1.1-.9 2-2 2H16"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M5.2 8h8.6c1.1 0 2 .9 2 2v8.8c0 1.1-.9 2-2 2H5.2c-1.1 0-2-.9-2-2V10c0-1.1.9-2 2-2z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// -------------------------
// Constants
// -------------------------
const TOAST_MS = 2600;

const STATIC_COUNTER_NAMES = [
  `Counter 1`,
  "Counter 1",
  "Counter 11",
  "Counter 22",
];

// -------------------------
// Helpers
// -------------------------
const getHeaderTitle = (view) => {
  switch (view) {
    case "theme":
      return "Change theme";
    case "names":
      return "Counter names";
    default:
      return "Settings";
  }
};

// -------------------------
// Component
// -------------------------
export default function CounterSettingsModal({
  onClose,
  themeId,
  onThemeChange,
  vibrationEnabled,
  onVibrationChange,
}) {
  // -------------------------
  // State
  // -------------------------
  const [view, setView] = useState("home");
  const [copiedName, setCopiedName] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);

  const modalRef = useRef(null);
  const toastTimerRef = useRef(null);

  // -------------------------
  // Effects
  // -------------------------
  useEffect(() => {
    const t = setTimeout(() => {
      modalRef.current?.querySelector(".iconButton")?.focus();
    }, 80);

    return () => clearTimeout(t);
  }, [view]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // -------------------------
  // Handlers
  // -------------------------
  const showToast = (message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);

    setToastMessage(message);

    toastTimerRef.current = setTimeout(() => {
      setToastMessage(null);
    }, TOAST_MS);
  };

  const handleCopy = async (name) => {
    try {
      await navigator.clipboard.writeText(name);

      setCopiedName(name);
      setTimeout(() => {
        setCopiedName((prev) => (prev === name ? null : prev));
      }, 2000);

      showToast("Copied");
    } catch {
      showToast("Copy failed");
    }
  };

  const handleThemePick = (theme) => {
    onThemeChange(theme.id);
    showToast("Theme applied");
  };

  const handleToggleVibration = () => {
    onVibrationChange?.(!vibrationEnabled);
  };

  // -------------------------
  // Render
  // -------------------------
  return (
    <>
      {/* Overlay */}
      <div
        className="overlay"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal */}
      <div
        className="counterSettingsModal show"
        role="dialog"
        aria-modal="true"
        aria-label={getHeaderTitle(view)}
        ref={modalRef}
      >
        {/* Header */}
        <div className="counterSettingsModal__header">
          <p>{getHeaderTitle(view)}</p>

          <button
            type="button"
            className="iconButton"
            onClick={onClose}
            aria-label="Close settings"
          >
            <img src={closeIcon} alt="" />
          </button>
        </div>

        {/* Back */}
        {view !== "home" && (
          <div className="counterSettingsModal__subnav">
            <button
              type="button"
              className="counterSettingsModal__backBtn"
              onClick={() => setView("home")}
            >
              ← Back
            </button>
          </div>
        )}

        {/* Body */}
        <div className="counterSettingsModal__body">
          {/* HOME */}
          {view === "home" && (
            <ul className="counterSettingsModal__menu">
              <li className="counterSettingsModal__toggleRow">
                <div className="counterSettingsModal__toggleCopy">
                  <span>Vibration on tap</span>
                  <span className="counterSettingsModal__toggleHint">
                    Short buzz on each tap
                  </span>
                </div>

                <button
                  type="button"
                  className={`counterSettingsModal__switch ${
                    vibrationEnabled ? "is-on" : ""
                  }`}
                  role="switch"
                  aria-checked={vibrationEnabled}
                  onClick={handleToggleVibration}
                />
              </li>

              <li>
                <button
                  className="counterSettingsModal__menuBtn"
                  onClick={() => setView("theme")}
                >
                  <span>Change theme</span>
                  <ChevronRightIcon />
                </button>
              </li>

              <li>
                <button
                  className="counterSettingsModal__menuBtn"
                  onClick={() => setView("names")}
                >
                  <span>Counter names</span>
                  <ChevronRightIcon />
                </button>
              </li>
            </ul>
          )}

          {/* THEME */}
          {view === "theme" && (
            <div className="counterSettingsModal__themeGrid">
              {COUNTER_THEMES.map((theme) => (
                <button
                  key={theme.id}
                  className={`counterSettingsModal__themeCard ${
                    themeId === theme.id ? "is-active" : ""
                  }`}
                  onClick={() => handleThemePick(theme)}
                >
                  <span
                    className={`counterSettingsModal__themeSwatch counterSettingsModal__themeSwatch--${theme.id}`}
                  />
                  <span>{theme.label}</span>
                  <span>{theme.description}</span>
                </button>
              ))}
            </div>
          )}

          {/* NAMES */}
          {view === "names" && (
            <ul className="counterSettingsModal__nameList">
              {STATIC_COUNTER_NAMES.map((name) => (
                <li key={name} className="counterSettingsModal__nameRow">
                  <span title={name}>{name}</span>

                  <button
                    className="counterSettingsModal__copyBtn"
                    onClick={() => handleCopy(name)}
                  >
                    <CopyIcon />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Screen reader feedback */}
        <p aria-live="polite" className="counterSettingsModal__a11yLive">
          {copiedName ? `Copied: ${copiedName}` : ""}
        </p>
      </div>

      {/* Toast */}
      {toastMessage && (
        <div className="undoToast undoToast--solo" role="status">
          <p>{toastMessage}</p>
        </div>
      )}
    </>
  );
}